(() => {
  const root = document.documentElement;
  let initialized = false;

  const init = () => {
    if (initialized) return true;

    const revealItems = [...document.querySelectorAll('[data-hydros-reveal]')];
    const countItems = [...document.querySelectorAll('[data-hydros-count]')];
    const videos = [...document.querySelectorAll('[data-hydros-video]')];
    const ugcDialogs = [...document.querySelectorAll('[data-hydros-ugc-dialog]')];

    if (!revealItems.length && !countItems.length && !videos.length && !ugcDialogs.length) return false;
    initialized = true;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const visibleVideos = new Set();
    const reveal = (element) => element.classList.add('is-revealed');

    const loadVideo = (video) => {
      if (video.dataset.hydrosLoaded) return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.hydrosLoaded = 'true';
      video.load();
    };

    const syncVideo = (video) => {
      if (motionQuery.matches || document.hidden || !visibleVideos.has(video) || ugcDialogs.some((dialog) => dialog.open)) {
        video.pause();
        return;
      }
      loadVideo(video);
      video.play().catch(() => {});
    };

    // Playback is controlled here so loading a source cannot bypass a pause.
    videos.forEach((video) => video.removeAttribute('autoplay'));
    document.addEventListener('visibilitychange', () => {
      videos.forEach(syncVideo);
      if (document.hidden) {
        ugcDialogs.forEach((dialog) => dialog.querySelector('[data-hydros-ugc-player]')?.pause());
      }
    });
    motionQuery.addEventListener('change', () => {
      if (motionQuery.matches) {
        root.classList.remove('hydros-motion-active');
        revealItems.forEach(reveal);
        videos.forEach((video) => { video.controls = true; });
      }
      videos.forEach(syncVideo);
    });

    const animateCount = (element) => {
      if (element.dataset.hydrosAnimated) return;
      element.dataset.hydrosAnimated = 'true';
      const target = Number.parseFloat(element.dataset.hydrosCount || '0');
      const from = Number.parseFloat(element.dataset.hydrosFrom || '0');
      const decimals = Number.parseInt(element.dataset.hydrosDecimals || '0', 10);
      const format = (value) => {
        if (value === 0) return '0';
        return value.toLocaleString('fr-FR', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      };

      if (motionQuery.matches || !Number.isFinite(target) || !Number.isFinite(from) || (target === 0 && from === 0)) {
        element.textContent = format(Number.isFinite(target) ? target : 0);
        return;
      }

      const start = performance.now();
      const duration = 720;
      const tick = (time) => {
        if (motionQuery.matches) {
          element.textContent = format(target);
          return;
        }
        const progress = Math.min(1, (time - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = format(from + (target - from) * eased);
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const initUgcDialogs = () => {
      ugcDialogs.forEach((dialog) => {
        const section = dialog.closest('.hydros-ugc-bubbles');
        const player = dialog.querySelector('[data-hydros-ugc-player]');
        const title = dialog.querySelector('[data-hydros-ugc-title]');
        const note = dialog.querySelector('[data-hydros-ugc-note]');
        const closeButton = dialog.querySelector('[data-hydros-ugc-close]');
        const openButtons = [...section.querySelectorAll('[data-hydros-ugc-open]')];

        const cleanPlayer = () => {
          player.pause();
          player.removeAttribute('src');
          player.removeAttribute('poster');
          player.classList.remove('is-landscape', 'is-routine');
          player.load();
          videos.forEach(syncVideo);
        };

        const closeDialog = () => {
          if (typeof dialog.close === 'function' && dialog.open) dialog.close();
          else {
            dialog.removeAttribute('open');
            cleanPlayer();
          }
        };

        openButtons.forEach((button) => {
          button.addEventListener('click', () => {
            videos.forEach((video) => video.pause());
            player.src = button.dataset.videoSrc;
            player.poster = button.dataset.videoPoster || '';
            player.classList.toggle('is-landscape', button.dataset.videoFormat === 'landscape');
            player.classList.toggle('is-routine', button.dataset.videoCrop === 'routine');
            title.textContent = button.dataset.videoTitle || '';
            note.textContent = button.dataset.videoNote || '';
            note.hidden = !button.dataset.videoNote;

            if (typeof dialog.showModal === 'function') dialog.showModal();
            else dialog.setAttribute('open', '');

            player.load();
            player.play().catch(() => {});
          });
        });

        closeButton.addEventListener('click', closeDialog);
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) closeDialog();
        });
        dialog.addEventListener('close', cleanPlayer);
      });
    };

    initUgcDialogs();

    if (typeof window.IntersectionObserver !== 'function') {
      revealItems.forEach(reveal);
      countItems.forEach(animateCount);
      videos.forEach((video) => {
        loadVideo(video);
        video.pause();
        video.controls = true;
        video.removeAttribute('autoplay');
      });
      root.classList.add('hydros-motion-ready');
      return true;
    }

    const revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -6% 0px' });

    const countObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateCount(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.55 });

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          visibleVideos.add(video);
          if (motionQuery.matches) {
            loadVideo(video);
            video.controls = true;
          }
        } else {
          visibleVideos.delete(video);
        }
        syncVideo(video);
      });
    }, { threshold: 0.35, rootMargin: '180px 0px' });

    revealItems.forEach((item) => revealObserver.observe(item));
    countItems.forEach((item) => countObserver.observe(item));
    videos.forEach((video) => videoObserver.observe(video));

    root.classList.add('hydros-motion-ready');
    if (motionQuery.matches) {
      revealItems.forEach(reveal);
      countItems.forEach(animateCount);
    } else {
      root.classList.add('hydros-motion-active');
    }

    requestAnimationFrame(() => {
      revealItems.forEach((item) => {
        const bounds = item.getBoundingClientRect();
        if (bounds.top < window.innerHeight * 0.94 && bounds.bottom > 0) reveal(item);
      });
    });

    window.setTimeout(() => revealItems.forEach(reveal), 3500);
    return true;
  };

  const start = () => {
    if (!init()) window.setTimeout(init, 60);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    window.setTimeout(start, 0);
  }
})();
